import { Button, Collapse, DatePicker, Form, FormInstance, Select, Space } from 'antd';
import { ContractInterface } from 'contract/type';
import React, { useState, useCallback, useEffect } from 'react';
import { CompanyInterface } from 'type/companyType';
import { companyApiService } from 'service/api/companyApiService';
import { contractService } from 'contract/services/contractService';
import { debounce } from 'lodash';

const { Option } = Select;

interface RevenueShareFilterProps {
  form: FormInstance;
  searchParams: URLSearchParams;
  handleFilterValueChange: (
    value: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => void;
  handleFilterSubmit: () => void;
  handleFilterReset: () => void;
}

export const RevenueShareFilter: React.FC<RevenueShareFilterProps> = ({
  form,
  handleFilterValueChange,
  handleFilterSubmit,
  handleFilterReset,
}: RevenueShareFilterProps) => {
  const [searchCompanies, setSearchCompanies] = useState<CompanyInterface[]>([]);
  const [defaultCompanies, setDefaultCompanies] = useState<CompanyInterface[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [searchContracts, setSearchContracts] = useState<ContractInterface[]>([]);
  const [defaultContracts, setDefaultContracts] = useState<ContractInterface[]>([]);
  const [contractSearchLoading, setContractSearchLoading] = useState(false);

  // Load default company list (100 items)
  useEffect(() => {
    const loadDefaultCompanies = async () => {
      console.log('🏢 Starting to load default companies...');
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page_size', '100');

        console.log('📡 API call with params:', queryParams.toString());
        const result = await companyApiService.fetch(queryParams);

        console.log('✅ Default companies loaded successfully:', {
          count: result.results.length,
          totalCount: result.count,
          firstCompany: result.results[0]?.title,
        });

        setDefaultCompanies(result.results);
        setSearchCompanies(result.results); // Initially set default list as search results too
      } catch (error) {
        console.error('❌ Failed to load default companies:', error);
      }
    };

    loadDefaultCompanies();
  }, []);

  // Load default contract list (100 items)
  useEffect(() => {
    const loadDefaultContracts = async () => {
      console.log('📋 Starting to load default contracts...');
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page_size', '100');

        console.log('📡 Contract API call with params:', queryParams.toString());
        const result = await contractService.fetch(queryParams);

        console.log('✅ Default contracts loaded successfully:', {
          count: result.results.length,
          totalCount: result.count,
          firstContract: result.results[0]?.title,
        });

        setDefaultContracts(result.results);
        setSearchContracts(result.results); // Initially set default list as search results too
      } catch (error) {
        console.error('❌ Failed to load default contracts:', error);
      }
    };

    loadDefaultContracts();
  }, []);

  // Contract search function (with debounce)
  const debouncedSearchContracts = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        // Show default list when search term is empty
        setSearchContracts(defaultContracts);
        return;
      }

      setContractSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await contractService.fetch(queryParams);
        setSearchContracts(result.results);
      } catch (error) {
        console.error('Contract search failed:', error);
        setSearchContracts([]);
      } finally {
        setContractSearchLoading(false);
      }
    }, 300),
    [defaultContracts]
  );

  // Restore default list when Contract Select box opens
  const handleContractDropdownVisibleChange = (open: boolean) => {
    if (open) {
      // Show default list each time Select box opens
      setSearchContracts(defaultContracts);
    }
  };

  // Handle Contract search term change
  const handleContractSearch = (value: string) => {
    if (!value.trim()) {
      // Show default list immediately when search term is empty
      setSearchContracts(defaultContracts);
    } else {
      debouncedSearchContracts(value);
    }
  };

  // Handle when search term is cleared via Contract X button
  const handleContractClear = () => {
    setSearchContracts(defaultContracts);
  };

  // Company search function (with debounce)
  const debouncedSearchCompanies = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        // Show default list when search term is empty
        setSearchCompanies(defaultCompanies);
        return;
      }

      setSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await companyApiService.fetch(queryParams);
        setSearchCompanies(result.results);
      } catch (error) {
        console.error('Company search failed:', error);
        setSearchCompanies([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    [defaultCompanies]
  );

  // Restore default list when Select box opens
  const handleDropdownVisibleChange = (open: boolean) => {
    if (open) {
      // Show default list each time Select box opens
      setSearchCompanies(defaultCompanies);
    }
  };

  // Handle search term change
  const handleSearch = (value: string) => {
    if (!value.trim()) {
      // Show default list immediately when search term is empty
      setSearchCompanies(defaultCompanies);
    } else {
      debouncedSearchCompanies(value);
    }
  };

  // Handle when search term is cleared via X button
  const handleClear = () => {
    setSearchCompanies(defaultCompanies);
  };

  // Company option rendering function
  const renderCompanyOption = (company: CompanyInterface) => {
    const titlePart = company.title_en ? `${company.title} (${company.title_en})` : company.title;
    const countryPart = company.country ? ` / ${company.country}` : '';
    const label = titlePart + countryPart;

    return (
      <Option key={company.id} value={company.id.toString()} title={label}>
        <span style={{ fontWeight: 500 }}>{label}</span>
      </Option>
    );
  };

  // Contract option rendering function
  const renderContractOption = (contract: ContractInterface) => {
    const titlePart = contract.title_en
      ? `${contract.title} (${contract.title_en})`
      : contract.title;

    return (
      <Option key={contract.id} value={contract.id.toString()} title={titlePart}>
        <span style={{ fontWeight: 500 }}>{titlePart}</span>
      </Option>
    );
  };

  return (
    <Collapse
      defaultActiveKey={['1']}
      style={{ marginBottom: 20 }}
      items={[
        {
          key: '1',
          label: 'Revenue Share Filter',
          children: (
            <Form form={form} layout="horizontal" onFinish={handleFilterSubmit}>
              <div>
                <Form.Item name="start_yearmon" label="Start Date">
                  <DatePicker
                    picker="month"
                    style={{ width: '10%' }}
                    placeholder="Select start date"
                    format="YYYY-MM"
                    onChange={e =>
                      handleFilterValueChange(prev => ({
                        ...prev,
                        start_yearmon: e ? e.format('YYYY-MM') : '',
                      }))
                    }
                  />
                </Form.Item>
                <Form.Item name="end_yearmon" label="End Date">
                  <DatePicker
                    picker="month"
                    style={{ width: '10%' }}
                    placeholder="Select end date"
                    format="YYYY-MM"
                    onChange={e =>
                      handleFilterValueChange(prev => ({
                        ...prev,
                        end_yearmon: e ? e.format('YYYY-MM') : '',
                      }))
                    }
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="content_provider_id"
                label="Content Provider"
                style={{ width: '50%' }}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Search content provider..."
                  filterOption={false}
                  onSearch={handleSearch}
                  onClear={handleClear}
                  loading={searchLoading}
                  notFoundContent={searchLoading ? 'Searching...' : 'No companies found'}
                  onChange={value =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      content_provider_id: value,
                    }))
                  }
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  onDropdownVisibleChange={handleDropdownVisibleChange}
                >
                  {searchCompanies.map(renderCompanyOption)}
                </Select>
              </Form.Item>

              <Form.Item name="contract_id" label="Contract" style={{ width: '40%' }}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Search contract..."
                  filterOption={false}
                  onSearch={handleContractSearch}
                  onClear={handleContractClear}
                  loading={contractSearchLoading}
                  notFoundContent={contractSearchLoading ? 'Searching...' : 'No contracts found'}
                  onChange={value =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      contract_id: value,
                    }))
                  }
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  onDropdownVisibleChange={handleContractDropdownVisibleChange}
                >
                  {searchContracts.map(renderContractOption)}
                </Select>
              </Form.Item>

              <Form.Item name="group_by" label="Group By">
                <Select
                  style={{ width: '200px' }}
                  placeholder="Group by"
                  onChange={value =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      group_by: value,
                    }))
                  }
                  options={[
                    { value: 'content', label: 'Content' },
                    { value: 'season', label: 'Season' },
                  ]}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Apply Filters
                  </Button>
                  <Button onClick={handleFilterReset}>Reset</Button>
                </Space>
              </Form.Item>
            </Form>
          ),
        },
      ]}
    />
  );
};
